import EditIcon from '@mui/icons-material/Edit'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import { SxProps, Theme } from '@mui/material/styles'
import { EditProfileDTO } from '@src/api/@types/EditProfileDTO'
import { FC, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'

interface TextFieldProps {
  handleInputChange: (field: keyof EditProfileDTO, value: string) => void
  styles: Record<string, SxProps<Theme>>
  addressValue: string
  emailValue: string
  phoneNumberValue: string
  addressError: string | null
  setAddressError: (error: string | null) => void
  emailError: string | null
  setEmailError: (error: string | null) => void
  phoneError: string | null
  setPhoneError: (error: string | null) => void
}

const TextFields: FC<TextFieldProps> = ({
  handleInputChange: handleChange,
  styles,
  addressValue,
  emailValue,
  phoneNumberValue,
  addressError,
  setAddressError,
  emailError,
  setEmailError,
  phoneError,
  setPhoneError
}) => {
  const { t } = useTranslation()

  const handleInput = (field: keyof EditProfileDTO, value: string) => {
    handleChange(field, value)
  }

  const handleAddressInput = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const addressPattern = /^[a-zA-Z0-9\s,'-]+,\s[a-zA-Z\s,'-]+,\s[A-Z]{2},\s[A-Z0-9]{3}\s[A-Z0-9]{3},\s[a-zA-Z\s,'-]+$/
    if (!addressPattern.test(value)) {
      setAddressError(t('userInfo.errorAddress'))
    } else {
      setAddressError(null)
    }
    handleInput('address', value)
  }

  const handleEmailInput = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailPattern.test(value)) {
      setEmailError(t('userInfo.errorEmail'))
    } else {
      setEmailError(null)
    }
    handleInput('email', value)
  }

  const handlePhoneInput = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const digitsOnly = value.replace(/\D/g, '')
    if (!/^\d+$/.test(digitsOnly)) {
      setPhoneError(t('userInfo.errorPhoneNumber'))
    } else {
      setPhoneError(null)
    }
    handleInput('phoneNumber', digitsOnly)
  }

  return (
    <>
      <TextField
        label={t('userInfo.address')}
        value={addressValue}
        onChange={handleAddressInput}
        variant="standard"
        slotProps={{
          input: {
            endAdornment: (
              <IconButton>
                <EditIcon sx={{ color: 'white' }} />
              </IconButton>
            ),
          },
        }}
        fullWidth
        sx={styles.textField}
        required
        error={!!addressError}
        helperText={addressError}
      />

      <TextField
        label={t('userInfo.email')}
        value={emailValue}
        onChange={handleEmailInput}
        variant="standard"
        slotProps={{
          input: {
            endAdornment: (
              <IconButton>
                <EditIcon sx={{ color: 'white' }} />
              </IconButton>
            ),
          },
        }}
        fullWidth
        sx={styles.textField}
        error={!!emailError}
        helperText={emailError}
        required
      />

      <TextField
        label={t('userInfo.phoneNumber')}
        value={phoneNumberValue}
        onChange={handlePhoneInput}
        variant="standard"
        slotProps={{
          input: {
            endAdornment: (
              <IconButton>
                <EditIcon sx={{ color: 'white' }} />
              </IconButton>
            ),
          },
        }}
        fullWidth
        error={!!phoneError}
        helperText={phoneError}
        sx={styles.textField}
        required
      />
    </>
  )
}

export default TextFields
